// src/pages/OrderTracking.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import generateInvoice from '../utils/generateInvoice';
import {
  Package, CheckCircle, Truck, Home, ClipboardCheck, XCircle,
  ArrowLeft, MapPin, Phone, CreditCard, Copy, Check, Clock, Boxes,
  FileText
} from 'lucide-react';

const OrderTracking = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [siteSettings, setSiteSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState('');

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const orderRef = doc(db, 'orders', id);
        const orderSnap = await getDoc(orderRef);
        if (orderSnap.exists()) {
          setOrder({ id: orderSnap.id, ...orderSnap.data() });
        }

        const siteRef = doc(db, 'settings', 'site');
        const siteSnap = await getDoc(siteRef);
        if (siteSnap.exists()) {
          setSiteSettings(siteSnap.data());
        }
      } catch (error) {
        console.error('Error fetching order:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
    window.scrollTo(0, 0);
  }, [id]);

  const steps = [
    { key: 'placed', label: 'Order Placed', icon: ClipboardCheck, description: 'Aapka order receive ho gaya' },
    { key: 'confirmed', label: 'Confirmed', icon: CheckCircle, description: 'Order confirm ho gaya' },
    { key: 'shipped', label: 'Shipped', icon: Boxes, description: 'Order ship kar diya gaya' },
    { key: 'out-for-delivery', label: 'Out for Delivery', icon: Truck, description: 'Delivery ke liye nikla' },
    { key: 'delivered', label: 'Delivered', icon: Home, description: 'Order deliver ho gaya' },
  ];

  const getCurrentStep = () => {
    const status = order?.status?.toLowerCase() || 'processing';
    if (status === 'processing' || status === 'placed') return 0;
    if (status === 'confirmed') return 1;
    if (status === 'shipped') return 2;
    if (status === 'out-for-delivery') return 3;
    if (status === 'completed' || status === 'delivered') return 4;
    if (status === 'cancelled') return -1;
    return 0;
  };

  const currentStep = getCurrentStep();
  const isCancelled = order?.status === 'cancelled';

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 1500);
  };

  const handleDownloadInvoice = () => {
    if (!order) return;
    generateInvoice(order, siteSettings);
  };

  const formatDate = (ts) => {
    if (!ts) return '-';
    try {
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      return d.toLocaleString('en-PK', {
        day: 'numeric', month: 'long', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return '-'; }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-body-bg)' }}>
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent"
          style={{ borderColor: 'var(--color-link)', borderTopColor: 'transparent' }}></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: 'var(--color-body-bg)' }}>
        <div className="text-center anim-fadeInUp max-w-md">
          <Package size={64} className="mx-auto mb-4 anim-float" style={{ color: 'var(--color-secondary-text)' }} />
          <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--color-primary-text)' }}>
            Order Not Found
          </h2>
          <p className="mb-6" style={{ color: 'var(--color-secondary-text)' }}>
            Ye order exist nahi karta.
          </p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold btn-2d shadow-lg"
            style={{ backgroundColor: 'var(--color-button-bg)', color: 'var(--color-button-text)' }}>
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const orderShortId = order.id.slice(-8).toUpperCase();

  return (
    <div className="min-h-screen py-6 anim-fadeIn" style={{ backgroundColor: 'var(--color-body-bg)' }}>
      <div className="max-w-4xl mx-auto px-4 md:px-6">

        {/* Header */}
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <button onClick={() => navigate(-1)}
            className="p-2 rounded-full btn-2d shrink-0"
            style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)`, color: 'var(--color-primary-text)' }}>
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold" style={{ color: 'var(--color-primary-text)' }}>
              Track Your Order
            </h1>
            <p className="text-xs md:text-sm flex items-center gap-1 flex-wrap" style={{ color: 'var(--color-secondary-text)' }}>
              Order ID: <span className="font-mono font-bold">#{orderShortId}</span>
              <button onClick={() => copyToClipboard(orderShortId, 'orderId')} className="ml-1">
                {copied === 'orderId' ? <Check size={12} className="text-green-600" /> : <Copy size={12} />}
              </button>
            </p>
          </div>

          {/* Invoice Button */}
          <button
            onClick={handleDownloadInvoice}
            className="flex items-center gap-2 px-3 py-2 rounded-lg font-semibold text-xs md:text-sm btn-2d shadow-md shrink-0"
            style={{ backgroundColor: 'var(--color-button-bg)', color: 'var(--color-button-text)' }}
          >
            <FileText size={16} />
            <span className="hidden sm:inline">Download Invoice</span>
            <span className="sm:hidden">Invoice</span>
          </button>
        </div>

        {/* Status Card */}
        <div className="rounded-2xl p-6 md:p-8 mb-6 relative overflow-hidden anim-scaleIn"
          style={{
            background: isCancelled
              ? 'linear-gradient(135deg, #dc2626 0%, #7f1d1d 100%)'
              : 'linear-gradient(135deg, #16a34a 0%, #14532d 100%)',
            color: '#fff',
          }}>
          <div className="absolute top-0 right-0 w-48 h-48 rounded-full bg-white opacity-5 -translate-y-1/2 translate-x-1/2"></div>

          <div className="relative z-10 flex items-start gap-4 flex-wrap">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center shrink-0 ${!isCancelled ? 'anim-pulseGlow' : ''}`}
              style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
              {isCancelled ? <XCircle size={32} /> : <Truck size={32} />}
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-sm opacity-90 mb-1">Current Status</p>
              <h2 className="text-2xl md:text-3xl font-bold mb-2 capitalize">
                {isCancelled ? 'Cancelled' : steps[currentStep]?.label || 'Processing'}
              </h2>
              <p className="text-sm opacity-90">
                {isCancelled
                  ? 'Ye order cancel kar diya gaya hai'
                  : steps[currentStep]?.description || 'Aapka order process ho raha hai'}
              </p>
            </div>
          </div>

          <div className="relative z-10 grid grid-cols-2 md:grid-cols-3 gap-3 mt-6 pt-5 border-t border-white/20">
            <div>
              <p className="text-xs opacity-80 mb-1">Order Date</p>
              <p className="text-sm font-semibold">{formatDate(order.createdAt)}</p>
            </div>
            {order.estimatedDelivery && (
              <div>
                <p className="text-xs opacity-80 mb-1">Est. Delivery</p>
                <p className="text-sm font-semibold">{order.estimatedDelivery}</p>
              </div>
            )}
            {order.trackingNumber && (
              <div>
                <p className="text-xs opacity-80 mb-1">Tracking #</p>
                <p className="text-sm font-semibold font-mono">{order.trackingNumber}</p>
              </div>
            )}
          </div>
        </div>

        {/* Timeline */}
        {!isCancelled && (
          <div className="rounded-2xl p-6 mb-6 anim-fadeInUp"
            style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}>
            <h3 className="text-lg font-bold mb-6 flex items-center gap-2" style={{ color: 'var(--color-primary-text)' }}>
              <Clock size={20} style={{ color: 'var(--color-link)' }} /> Order Progress
            </h3>

            <div className="relative">
              {steps.map((step, i) => {
                const StepIcon = step.icon;
                const completed = i <= currentStep;
                const isCurrent = i === currentStep;
                const isLast = i === steps.length - 1;

                return (
                  <div key={step.key} className="flex gap-4 relative pb-6 last:pb-0">
                    {!isLast && (
                      <div className="absolute left-6 top-12 w-0.5 h-full -translate-x-1/2"
                        style={{
                          backgroundColor: i < currentStep ? 'var(--color-accent)' : 'var(--color-border)',
                          transition: 'background-color 0.5s',
                        }}></div>
                    )}

                    <div className="relative z-10 shrink-0">
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-500 ${isCurrent ? 'anim-pulseGlow' : ''}`}
                        style={{
                          backgroundColor: completed ? 'var(--color-accent)' : 'var(--color-card-bg)',
                          border: completed ? 'none' : `2px solid var(--color-border)`,
                          color: completed ? '#fff' : 'var(--color-secondary-text)',
                          boxShadow: completed ? '0 4px 12px rgba(22, 163, 74, 0.3)' : 'none',
                        }}>
                        <StepIcon size={20} />
                      </div>
                    </div>

                    <div className="flex-1 pt-2">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h4 className="font-bold text-base"
                          style={{ color: completed ? 'var(--color-primary-text)' : 'var(--color-secondary-text)' }}>
                          {step.label}
                        </h4>
                        {isCurrent && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                            style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}>
                            CURRENT
                          </span>
                        )}
                        {i < currentStep && <CheckCircle size={14} style={{ color: 'var(--color-accent)' }} />}
                      </div>
                      <p className="text-xs md:text-sm" style={{ color: 'var(--color-secondary-text)' }}>
                        {step.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Order Items */}
        <div className="rounded-2xl p-6 mb-6 anim-fadeInUp"
          style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}>
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--color-primary-text)' }}>
            <Package size={20} style={{ color: 'var(--color-link)' }} /> Order Items ({order.items?.length || 0})
          </h3>

          <div className="space-y-3">
            {order.items?.map((item, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-lg"
                style={{ backgroundColor: 'var(--color-body-bg)' }}>
                <img src={item.image} alt={item.title}
                  className="w-16 h-16 object-cover rounded-lg shrink-0" />
                <div className="flex-1 min-w-0">
                  <Link to={`/product/${item.id}`}
                    className="font-semibold text-sm line-clamp-2 hover:opacity-80"
                    style={{ color: 'var(--color-primary-text)' }}>
                    {item.title}
                  </Link>
                  <p className="text-xs mt-1" style={{ color: 'var(--color-secondary-text)' }}>
                    Qty: {item.quantity} × Rs. {item.price}
                  </p>
                </div>
                <p className="font-bold text-sm shrink-0" style={{ color: 'var(--color-link)' }}>
                  Rs. {item.price * item.quantity}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Delivery + Payment */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="rounded-2xl p-6 anim-fadeInUp"
            style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}>
            <h3 className="font-bold mb-3 flex items-center gap-2 text-base" style={{ color: 'var(--color-primary-text)' }}>
              <MapPin size={18} style={{ color: 'var(--color-link)' }} /> Shipping Address
            </h3>
            <div className="space-y-2 text-sm">
              <p className="font-semibold" style={{ color: 'var(--color-primary-text)' }}>
                {order.customerName || '-'}
              </p>
              <p style={{ color: 'var(--color-secondary-text)' }}>
                {order.address}
                {order.city && `, ${order.city}`}
                {order.state && `, ${order.state}`}
                {order.postalCode && ` - ${order.postalCode}`}
              </p>
              <p className="flex items-center gap-2 text-xs" style={{ color: 'var(--color-secondary-text)' }}>
                <Phone size={12} /> {order.phone || '-'}
              </p>
            </div>
          </div>

          <div className="rounded-2xl p-6 anim-fadeInUp"
            style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}>
            <h3 className="font-bold mb-3 flex items-center gap-2 text-base" style={{ color: 'var(--color-primary-text)' }}>
              <CreditCard size={18} style={{ color: 'var(--color-link)' }} /> Payment Info
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span style={{ color: 'var(--color-secondary-text)' }}>Method</span>
                <span className="font-semibold" style={{ color: 'var(--color-primary-text)' }}>
                  {order.paymentMethod === 'cod' ? 'Cash on Delivery' : 'Bank Transfer'}
                </span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: 'var(--color-secondary-text)' }}>Status</span>
                <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                  order.paymentStatus === 'paid'
                    ? 'bg-green-100 text-green-700'
                    : order.paymentStatus === 'pending-verification'
                    ? 'bg-yellow-100 text-yellow-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  {order.paymentStatus === 'paid' ? 'PAID'
                    : order.paymentStatus === 'pending-verification' ? 'PENDING'
                    : 'UNPAID'}
                </span>
              </div>
              {order.couponCode && (
                <div className="flex justify-between">
                  <span style={{ color: 'var(--color-secondary-text)' }}>Coupon</span>
                  <span className="font-mono font-semibold" style={{ color: 'var(--color-link)' }}>
                    {order.couponCode}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Price Breakdown */}
        <div className="rounded-2xl p-6 anim-fadeInUp"
          style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}>
          <h3 className="font-bold mb-4 text-base" style={{ color: 'var(--color-primary-text)' }}>
            Price Breakdown
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span style={{ color: 'var(--color-secondary-text)' }}>Subtotal</span>
              <span className="font-semibold" style={{ color: 'var(--color-primary-text)' }}>Rs. {order.subtotal || 0}</span>
            </div>
            {order.discount > 0 && (
              <div className="flex justify-between">
                <span className="text-green-600">Discount</span>
                <span className="font-semibold text-green-600">- Rs. {order.discount}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span style={{ color: 'var(--color-secondary-text)' }}>Shipping</span>
              <span className="font-semibold" style={{ color: order.shipping === 0 ? 'var(--color-link)' : 'var(--color-primary-text)' }}>
                {order.shipping === 0 ? 'FREE' : `Rs. ${order.shipping}`}
              </span>
            </div>
            {order.tax > 0 && (
              <div className="flex justify-between">
                <span style={{ color: 'var(--color-secondary-text)' }}>
                  {order.taxName || 'Tax'} {order.taxRate ? `(${order.taxRate}%)` : ''}
                </span>
                <span className="font-semibold" style={{ color: 'var(--color-primary-text)' }}>Rs. {order.tax}</span>
              </div>
            )}
            <div className="flex justify-between pt-3 border-t text-lg" style={{ borderColor: 'var(--color-border)' }}>
              <span className="font-bold" style={{ color: 'var(--color-primary-text)' }}>Total</span>
              <span className="font-bold" style={{ color: 'var(--color-link)' }}>Rs. {order.total}</span>
            </div>
          </div>
        </div>

        <div className="h-20 md:h-0"></div>
      </div>
    </div>
  );
};

export default OrderTracking;